import React, {
  useState, useRef, useEffect, useMemo,
} from 'react';
import LookPreviewCanvas from '../shared/LookPreviewCanvas';
import { deleteLook, setLookActive, fetchGifCampaigns } from '../shared/api';
import { FullPageSpinner } from '../shared/Spinner';

// Lazy-load wrapper — only renders children when scrolled into view
const LazyRender = ({ children, height }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { rootMargin: '200px' }, // start loading 200px before scrolling into view
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (visible) return <>{children}</>;
  return <div ref={ref} style={{ minHeight: height, background: '#f5f5f5', borderRadius: 4 }} />;
};

// A look with no `active` key (an older cached payload) is treated as ON —
// matches the column default, so nothing reads as parked by accident.
const isActive = (look) => look.active !== false;

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
];

const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'season', label: 'Season' },
  { value: 'purchases_desc', label: 'Purchases (high-low)' },
  { value: 'purchases_asc', label: 'Purchases (low-high)' },
  { value: 'updated', label: 'Recently Updated' },
];

const ManageLooks = ({
  looks, loading, onEdit, onDuplicate, onDeleted, onActiveChanged, onNew, purchaseCounts,
}) => {
  const [filter, setFilter] = useState('');
  const [seasonFilter, setSeasonFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [deleting, setDeleting] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [campaigns, setCampaigns] = useState([]);

  const getPurchases = (look) => (purchaseCounts || {})[look.id] || 0;

  const seasons = useMemo(
    () => [...new Set(looks.map((l) => l.season).filter(Boolean))].sort(),
    [looks],
  );

  // Reload campaigns whenever the season filter changes so the campaign
  // dropdown scopes to just that season's set (server does the filter).
  useEffect(() => {
    let cancelled = false;
    fetchGifCampaigns(seasonFilter || undefined)
      .then((list) => { if (!cancelled) setCampaigns(list); })
      .catch(() => { if (!cancelled) setCampaigns([]); });
    return () => { cancelled = true; };
  }, [seasonFilter]);

  const campaignById = useMemo(() => {
    const m = {};
    campaigns.forEach((c) => { m[c.id] = c; });
    return m;
  }, [campaigns]);

  // When the season filter changes to something incompatible with the
  // current campaign filter, drop the campaign filter so we don't wind up
  // with an empty grid the user can't explain.
  useEffect(() => {
    if (campaignFilter && !campaignById[campaignFilter]) setCampaignFilter('');
  }, [campaignById, campaignFilter]);

  const sorted = useMemo(() => {
    let list = looks;
    if (filter || seasonFilter || statusFilter || campaignFilter) {
      list = looks.filter((l) => {
        if (filter && !l.name.toLowerCase().includes(filter.toLowerCase())) return false;
        if (seasonFilter && l.season !== seasonFilter) return false;
        if (statusFilter === 'active' && !isActive(l)) return false;
        if (statusFilter === 'inactive' && isActive(l)) return false;
        if (campaignFilter && String(l.promo_gif_campaign_id || '') !== String(campaignFilter)) return false;
        return true;
      });
    }
    return [...list].sort((a, b) => {
      switch (sortBy) {
        case 'name': return (a.name || '').localeCompare(b.name || '');
        case 'season': return (a.season || '').localeCompare(b.season || '') || (a.name || '').localeCompare(b.name || '');
        case 'purchases_desc': return getPurchases(b) - getPurchases(a);
        case 'purchases_asc': return getPurchases(a) - getPurchases(b);
        case 'updated': return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
        default: return 0;
      }
    });
  }, [looks, filter, seasonFilter, statusFilter, campaignFilter, sortBy, purchaseCounts]);

  const inactiveShown = useMemo(() => sorted.filter((l) => !isActive(l)).length, [sorted]);

  const handleDelete = async (look) => {
    if (!confirm(`Delete "${look.name}"? This cannot be undone.`)) return;
    setDeleting(look.id);
    try {
      await deleteLook(look.id);
      onDeleted(look.id);
    } catch (err) {
      alert(`Delete failed: ${err.response?.data?.errors?.join(', ') || err.message}`);
    }
    setDeleting(null);
  };

  // Deactivating drops the look from the season's exported CSV, so EC2 stops
  // rendering it for newly-processed students and the store stops offering it.
  // The server re-exports the artifact; surface its refusal rather than
  // leaving the card showing a state the backend rejected.
  const handleToggleActive = async (look) => {
    const next = !isActive(look);
    if (!next && !confirm(
      `Deactivate "${look.name}"?\n\n` +
      'EC2 will stop creating it for newly-processed students and it will no ' +
      'longer be offered in the store. Orders already placed are unaffected.'
    )) return;

    setToggling(look.id);
    try {
      const res = await setLookActive(look.id, next);
      onActiveChanged(look.id, res.active);
    } catch (err) {
      alert(err.response?.data?.error || `Could not update "${look.name}": ${err.message}`);
    }
    setToggling(null);
  };

  if (loading) {
    return <FullPageSpinner label="Loading looks..." />;
  }

  return (
    <div style={{ padding: 24, height: '100%', overflow: 'auto' }}>
      <div style={{
        display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center',
      }}
      >
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search by name..."
          style={{
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: 4,
            fontSize: 13,
            width: 250,
          }}
        />
        <select
          value={seasonFilter}
          onChange={(e) => setSeasonFilter(e.target.value)}
          style={{
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: 4,
            fontSize: 13,
            background: '#fff',
          }}
        >
          <option value="">All seasons</option>
          {seasons.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {campaigns.length > 0 && (
          <select
            value={campaignFilter}
            onChange={(e) => setCampaignFilter(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: 4,
              fontSize: 13,
              background: '#fff',
            }}
          >
            <option value="">All campaigns</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {' '}
                (
                {c.slug}
                )
              </option>
            ))}
          </select>
        )}
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{
            padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4,
            fontSize: 13, background: '#fff'
          }}
        >
          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          style={{
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: 4,
            fontSize: 13,
            background: '#fff',
          }}
        >
          {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 13, color: '#666' }}>
          {sorted.length}
          {' '}
          look
          {sorted.length !== 1 ? 's' : ''}
          {inactiveShown > 0 && (
            <span style={{ color: '#b26500' }}>
              {' '}
              &middot;
              {' '}
              {inactiveShown}
              {' '}
              inactive
            </span>
          )}
        </span>
      </div>

      {sorted.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 16, color: '#999', marginBottom: 12 }}>
            {looks.length === 0 ? 'No look templates yet' : 'No looks match your filter'}
          </div>
          <button
            onClick={onNew}
            style={{
              padding: '10px 24px',
              background: '#1976d2',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            Create First Look
          </button>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 16,
        }}
        >
          {sorted.map((look) => {
            const active = isActive(look);
            const busy = toggling === look.id;
            const aspect = (look.canvas_width || 2400) / (look.canvas_height || 3000);
            const thumbW = aspect >= 1 ? 200 : Math.round(160 * aspect);
            const thumbH = aspect >= 1 ? Math.round(200 / aspect) : 200;

            return (
              <div
                key={look.id}
                style={{
                  border: active ? '1px solid #e0e0e0' : '1px solid #e0b980',
                  borderRadius: 6,
                  background: active ? '#fff' : '#fffaf2',
                  overflow: 'hidden',
                  transition: 'box-shadow .15s',
                  cursor: 'pointer',
                }}
                onClick={() => onEdit(look)}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,.12)'}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
              >
                {/* Large preview thumbnail — lazy loaded */}
                <div style={{
                  background: '#f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 8,
                  minHeight: 160,
                }}
                >
                  <LazyRender height={thumbH}>
                    <div style={{ opacity: active ? 1 : 0.4, filter: active ? 'none' : 'grayscale(1)' }}>
                    <LookPreviewCanvas
                      look={look}
                      width={thumbW}
                      height={thumbH}
                      allLooks={looks}
                    />
                    </div>
                  </LazyRender>
                </div>

                {/* Look info */}
                <div style={{ padding: '8px 12px' }}>
                  <div style={{
                    fontSize: 14, fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: 6,
                  }}
                  >
                    {look.name}
                    {look.look_type === 'collage' && (
                      <span style={{
                        fontSize: 9,
                        color: '#fff',
                        background: '#9c27b0',
                        borderRadius: 2,
                        padding: '1px 4px',
                        fontWeight: 500,
                      }}
                      >
                        Collage
                      </span>
                    )}
                    {look.look_type === 'gif_slide' && (
                      <span style={{
                        fontSize: 9,
                        color: '#fff',
                        background: '#0288d1',
                        borderRadius: 2,
                        padding: '1px 4px',
                        fontWeight: 500,
                      }}
                      >
                        GIF
                      </span>
                    )}
                    {!active && (
                      <span
                        title="Not in the render set — EC2 skips it and the store does not offer it"
                        style={{
                          fontSize: 9, color: '#fff', background: '#b26500',
                          borderRadius: 2, padding: '1px 4px', fontWeight: 500
                        }}
                      >Inactive</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                    {look.season || 'no season'}
                    {' '}
                    |
                    {look.pose_number || 'no pose'}
                    {' '}
                    |
                    {look.canvas_width}
                    x
                    {look.canvas_height}
                    {(look.text_fields || []).length > 0 && ` | ${(look.text_fields || []).length} text`}
                  </div>
                  {look.look_type === 'gif_slide' && look.promo_gif_campaign_id && (
                    <div style={{
                      fontSize: 10, color: '#0288d1', marginTop: 2, fontWeight: 500,
                    }}
                    >
                      {campaignById[look.promo_gif_campaign_id]?.name || `Campaign #${look.promo_gif_campaign_id}`}
                    </div>
                  )}
                  {getPurchases(look) > 0 && (
                    <div style={{
                      fontSize: 10, color: '#2e7d32', fontWeight: 600, marginTop: 2,
                    }}
                    >
                      {getPurchases(look)}
                      {' '}
                      purchase
                      {getPurchases(look) !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div style={{
                  display: 'flex', borderTop: '1px solid #f0f0f0', background: '#fafafa',
                }}
                >
                  <button
                    onClick={(e) => { e.stopPropagation(); onEdit(look); }}
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: 'none',
                      background: 'none',
                      fontSize: 12,
                      color: '#1976d2',
                      cursor: 'pointer',
                      fontWeight: 500,
                    }}
                  >
                    Edit
                  </button>
                  {onDuplicate && (
                    <>
                      <div style={{ width: 1, background: '#e0e0e0' }} />
                      <button
                        onClick={(e) => { e.stopPropagation(); onDuplicate(look); }}
                        style={{
                          flex: 1,
                          padding: '8px',
                          border: 'none',
                          background: 'none',
                          fontSize: 12,
                          color: '#00695c',
                          cursor: 'pointer',
                          fontWeight: 500,
                        }}
                      >
                        Duplicate
                      </button>
                    </>
                  )}
                  <div style={{ width: 1, background: '#e0e0e0' }} />
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(look); }}
                    disabled={deleting === look.id}
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: 'none',
                      background: 'none',
                      fontSize: 12,
                      color: '#d32f2f',
                      cursor: 'pointer',
                      fontWeight: 500,
                    }}
                  >
                    {deleting === look.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>

                {/* Render-set toggle — its own row so the label has room to be
                    explicit. Separate from publish: this survives Publish All. */}
                <button
                  onClick={(e) => { e.stopPropagation(); handleToggleActive(look); }}
                  disabled={busy}
                  title={active
                    ? 'Stop EC2 rendering this look and remove it from the store'
                    : 'Put this look back in the render set'}
                  style={{
                    width: '100%', padding: '8px', border: 'none',
                    borderTop: '1px solid #f0f0f0',
                    background: active ? '#fafafa' : '#fff3e0',
                    fontSize: 12, fontWeight: 500,
                    color: active ? '#b26500' : '#2e7d32',
                    cursor: busy ? 'wait' : 'pointer'
                  }}
                >
                  {busy ? 'Saving...' : (active ? 'Deactivate' : 'Activate')}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ManageLooks;
